import {
  fromPairs,
  isArray,
  isEmpty,
  isNil,
  isNumber,
  isObject,
  isString,
  toPairs,
} from 'lodash';

export { updatedDiff } from 'deep-object-diff';
export { detailedDiff } from 'deep-object-diff';
export { default as cloneDeep } from 'lodash/cloneDeep';
export { default as difference } from 'lodash/difference';
export { default as differenceBy } from 'lodash/differenceBy';
export { default as first } from 'lodash/first';
export { default as intersection } from 'lodash/intersection';
export { default as isEmpty } from 'lodash/isEmpty';
export { default as isEqual } from 'lodash/isEqual';
export { default as isNull } from 'lodash/isNull';
export { default as isNumber } from 'lodash/isNumber';
export { default as isUndefined } from 'lodash/isUndefined';
export { default as keyBy } from 'lodash/keyBy';
export { default as mapKeys } from 'lodash/mapKeys';
export { default as mapValues } from 'lodash/mapValues';
export { default as mean } from 'lodash/mean';
export { default as merge } from 'lodash/merge';
export { default as omit } from 'lodash/omit';
export { default as omitBy } from 'lodash/omitBy';
export { default as orderBy } from 'lodash/orderBy';
export { default as pick } from 'lodash/pick';
export { default as pickBy } from 'lodash/pickBy';
export { default as range } from 'lodash/range';
export { default as trim } from 'lodash/trim';
export { default as uniq } from 'lodash/uniq';
export { v4 as generateUuid } from 'uuid';

import { default as isEqual } from 'lodash/isEqual';
import { default as keyBy } from 'lodash/keyBy';
import { default as omit } from 'lodash/omit';
import { default as omitBy } from 'lodash/omitBy';

export const isNullable = (value: unknown): value is null | undefined => {
  return value == null;
};

export const isTruthy = (value: unknown): boolean => {
  return Boolean(value);
};

export const keyById = <T extends { id: number | string }>(
  array: T[] //
): { [key: string]: T } => {
  return keyBy<T>(array, ({ id }) => id);
};

export const keyByIdWithOmitId = <T extends { id: number | string }>(
  array: T[] //
): { [key: string]: Omit<T, 'id'> } => {
  return fromPairs(
    toPairs(keyById(array)).map(([key, value]) => [key, omit(value, 'id')])
  );
};

export const mapById = <T extends { id: ID }, ID>(
  array: T[] //
): ID[] => {
  return array.map(({ id }) => id);
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const omitNullish = <T extends object>(object: T): Partial<T> => {
  return omitBy<T>(object, isNullable);
};

export const rejectBy = <T>(
  array: T[], //
  fn: (val: T, index: number) => boolean
): T[] => {
  return array.filter((value, index) => !fn(value, index));
};

export const reject = <T>(
  array: T[], //
  value: T
): T[] => {
  return rejectBy(array, (item) => item === value);
};

export const rejectById = <T extends { id: ID }, ID>(
  array: T[], //
  id: ID
): T[] => {
  return rejectBy(array, (item) => item.id === id);
};

export const rejectByIndex = <T>(
  array: T[], //
  index: number
): T[] => {
  return rejectBy(array, (_, _index) => _index === index);
};

export const rejectNullable = <T>(array: T[]): NonNullable<T>[] => {
  return rejectBy(array, isNullable) as NonNullable<T>[];
};

export const findById = <I extends { id: ID }, ID>(
  array: I[],
  id: ID
): undefined | I => {
  return array.find((item) => item.id === id);
};

export const findByKey = <I extends { key: K }, K>(
  array: I[],
  key: K
): undefined | I => {
  return array.find((item) => item.key === key);
};

export const findByCode = <I extends { code: C }, C>(
  array: I[],
  code: C
): undefined | I => {
  return array.find((item) => item.code === code);
};

export const findByCodes = <I extends { code: C }, C>(
  array: I[],
  codes: C[]
): undefined | I => {
  return array.find((item) => codes.includes(item.code));
};

export const findByType = <I extends { type: T }, T>(
  array: I[],
  type: T
): undefined | I => {
  return array.find((item) => item.type === type);
};

export const findByCategory = <I extends { category: T }, T>(
  array: I[],
  category: T
): undefined | I => {
  return array.find((item) => item.category === category);
};

export const findDefault = <I extends { isDefault: boolean }>(
  array: I[]
): undefined | I => {
  return array.find((item) => item.isDefault);
};

export const findIndex = <T>(
  array: T[],
  prediate: (value: T, index: number) => boolean
): undefined | number => {
  const index = array.findIndex(prediate);
  if (index === -1) {
    return undefined;
  }

  return index;
};

export const findIndexById = <T extends { id: ID }, ID>(
  array: T[],
  id: ID
): undefined | number => {
  return findIndex(array, (item) => item.id === id);
};

export const alwaysArray = <T>(value: T | ConcatArray<T>): T[] => {
  return ([] as T[]).concat(value);
};

export const normalizeCommaSeparatedValue = (
  value?: string
): string | undefined => {
  const values = value
    ?.split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  return values?.length ? Array.from(new Set(values)).join(',') : undefined;
};

export const commaSeparatedValueToArray = (
  value?: string
): string[] | undefined => {
  const normalizedValue = normalizeCommaSeparatedValue(value);

  return normalizedValue?.split(',');
};

export const alwaysFirst = <T>(val: T | T[]): T => {
  return alwaysArray(val)[0];
};

export const fileToUrl = (file: File): string => {
  return URL.createObjectURL(file);
};

export const objectsGetDifference = (
  leftObject: Record<string, unknown> | undefined,
  rightObject: Record<string, unknown> | undefined
): Record<string, unknown> => {
  if (!rightObject) {
    return {};
  }

  return omitBy(leftObject, (value, key) => isEqual(value, rightObject[key]));
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const urlToFile = async (
  url: string,
  fileName = 'untitled'
): Promise<File> => {
  const blob = await fetch(url).then((response) => response.blob());

  return new File([blob], fileName, {
    lastModified: new Date().getTime(),
    type: blob.type,
  });
};

export const last = <T>(array: T[]): T => {
  return array.slice(-1)[0];
};

export const numberOrNothing = (value: unknown): undefined | number => {
  if (value == null || isNaN(Number(value))) {
    return undefined;
  }

  return Number(value);
};

export const readJson = <T>(
  jsonString: string,
  // eslint-disable-next-line @typescript-eslint/ban-types
  converter?: (object: unknown) => object | null
): undefined | T => {
  try {
    const result: T = JSON.parse(jsonString);

    if (!converter) {
      return result;
    }

    return converter(result) as T;
  } catch (error) {
    console.error(error);

    return undefined;
  }
};

export const nonUndefinedOr = <A, B>(a: A, b: B): A | B => {
  return a !== undefined ? a : b;
};

export const convertTrueFalseStringToBoolean = (
  value: boolean | number | string | undefined
): boolean | number | string | undefined => {
  let processedValue = value;

  switch (value) {
    case 'true':
      processedValue = true;
      break;
    case 'false':
      processedValue = false;
      break;
    default:
  }

  return processedValue;
};

export const clamp = (number: number, min: number, max: number) =>
  Math.min(Math.max(number, min), max);

export const clampToPercent = (number: number) => clamp(number, 0, 100);

export const summarizeArrayValues = (array: number[]): number => {
  return array.reduce((sum, current) => sum + current, 0);
};

export const isValueExists = (value: unknown) => {
  if (isNil(value)) {
    return false;
  }

  if (isString(value)) {
    return value.trim().length;
  }

  if (isNumber(value)) {
    return true;
  }

  if (isArray(value)) {
    return value.length;
  }

  if (isObject(value)) {
    return Object.keys(value).length;
  }

  return false;
};

export const isArrayNotEmpty = (value: unknown) => {
  return isArray(value) && !isEmpty(value);
};

export const truncateString = (value: string, maxLength: number): string => {
  if (!value) {
    return '';
  }

  if (value.length <= maxLength) {
    return value;
  }

  return value.slice(0, maxLength) + '...';
};

export const isDigits = (chars: string): boolean => {
  return /^[0-9]+$/.test(chars);
};

export const isNonEmptyDigitsValue = (value: string): boolean => {
  const normalizedValue = value.trim();

  return Boolean(normalizedValue) && isDigits(normalizedValue);
};
